if (!customElements.get('complete-the-look')) {
  class CompleteTheLook extends HTMLElement {
    constructor() {
      super();
      this.container = this.querySelector('.complete-the-look__products');
      this.prevButton = this.querySelector('.complete-the-look__arrow--prev');
      this.nextButton = this.querySelector('.complete-the-look__arrow--next');
      this.nav = this.querySelector('.complete-the-look__nav');
      this.productHandle = this.dataset.productHandle;
      this.productsToShow = parseInt(this.dataset.productsToShow, 10) || 8;
      this.searchUrl = this.dataset.searchUrl;
      this.handleResize = this.updateNavigation.bind(this);
      this.handleScroll = this.updateNavigation.bind(this);
    }

    connectedCallback() {
      if (!this.container || !this.searchUrl) {
        return;
      }

      this.styleTags = this.getStyleTags();

      if (!this.styleTags.length) {
        this.remove();
        return;
      }

      this.hidden = false;
      this.bindNavigation();
      this.abortController = new AbortController();
      this.loadProducts();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.removeEventListener('resize', this.handleResize);
      this.container?.removeEventListener('scroll', this.handleScroll);
    }

    bindNavigation() {
      if (!this.prevButton || !this.nextButton || !this.container) {
        return;
      }

      this.prevButton.addEventListener('click', () => this.scrollByCard(-1));
      this.nextButton.addEventListener('click', () => this.scrollByCard(1));
      this.container.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);
    }

    getStyleTags() {
      try {
        const tags = JSON.parse(this.dataset.productTags || '[]');

        return [...new Set(tags
          .filter((tag) => typeof tag === 'string')
          .map((tag) => tag.trim())
          .filter((tag) => tag.toLowerCase().startsWith('style_')))];
      } catch (error) {
        console.error(error);
        return [];
      }
    }

    buildUrl() {
      const params = new URLSearchParams({
        view: 'complete-the-look',
        type: 'product',
        q: this.styleTags.map((tag) => `tag:${tag}`).join(' OR ')
      });

      return `${this.searchUrl}?${params.toString()}`;
    }

    async loadProducts() {
      try {
        const response = await fetch(this.buildUrl(), { signal: this.abortController.signal });
        const text = await response.text();
        const html = document.createElement('div');

        html.innerHTML = text;

        const items = Array.from(html.querySelectorAll('.complete-the-look__item'));
        const seenHandles = new Set([this.productHandle]);
        const fragment = document.createDocumentFragment();
        let matchedCount = 0;

        items.forEach((item) => {
          if (matchedCount >= this.productsToShow) {
            return;
          }

          const handle = item.dataset.productHandle;

          if (!handle || seenHandles.has(handle)) {
            return;
          }

          seenHandles.add(handle);
          fragment.appendChild(item);
          matchedCount += 1;
        });

        if (!matchedCount) {
          this.remove();
          return;
        }

        this.container.innerHTML = '';
        this.container.appendChild(fragment);
        this.dataset.loading = 'false';
        this.classList.add('product-recommendations--loaded');
        this.updateNavigation();

        document.dispatchEvent(new CustomEvent('product:recommendations-loaded', {
          detail: {
            container: this
          }
        }));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      }
    }

    scrollByCard(direction) {
      const firstItem = this.container.querySelector('.complete-the-look__item');

      if (!firstItem) {
        return;
      }

      const gap = parseFloat(window.getComputedStyle(this.container).columnGap || window.getComputedStyle(this.container).gap || 0);
      const distance = firstItem.getBoundingClientRect().width + gap;

      this.container.scrollBy({
        left: distance * direction,
        behavior: 'smooth'
      });
    }

    updateNavigation() {
      if (!this.container || !this.prevButton || !this.nextButton || !this.nav) {
        return;
      }

      const maxScrollLeft = this.container.scrollWidth - this.container.clientWidth;
      const hasOverflow = maxScrollLeft > 1;
      const scrollLeft = this.container.scrollLeft;
      const itemCount = this.container.querySelectorAll('.complete-the-look__item').length;
      const visibleCount = window.matchMedia('(max-width: 767px)').matches ? 2 : 4;
      const shouldShowNav = itemCount > visibleCount && hasOverflow;

      this.nav.hidden = !shouldShowNav;
      this.prevButton.disabled = !shouldShowNav || scrollLeft <= 1;
      this.nextButton.disabled = !shouldShowNav || scrollLeft >= maxScrollLeft - 1;
    }
  }

  customElements.define('complete-the-look', CompleteTheLook);
}
